// Portal › Kullanıcılar — satır parçaları (M16 T20; portal turu 2026-10-06 P2-10): durum rozeti,
// KPI erişim özeti ve ≤1100 px KART düzeni (ImportRowsTable 1A deseni). ClientUsers.tsx ≤600 satır
// kuralı için ayrıldı; kopyalar lib/client-admin-copy.ts TEK kaynak.

import { CLIENT_ADMIN_ROLE_LABELS, type ClientUser } from "@mahir/shared";
import { CLIENT_ADMIN_COPY as C } from "../../lib/client-admin-copy";
import { accessSummaryLine } from "../../lib/client-kpi-access-ui";

const STATUS_STYLE: Record<ClientUser["status"], { bg: string; fg: string }> = {
  invited: {
    bg: "color-mix(in srgb, var(--color-warning-text) 14%, white)",
    fg: "var(--color-warning-text)",
  },
  active: { bg: "color-mix(in srgb, var(--color-primary) 12%, white)", fg: "var(--color-primary)" },
  expired: { bg: "color-mix(in srgb, var(--color-text-3) 14%, white)", fg: "var(--color-text-3)" },
  closed: { bg: "color-mix(in srgb, var(--color-text-3) 14%, white)", fg: "var(--color-text-3)" },
};

export function StatusPill({ status }: { status: ClientUser["status"] }): JSX.Element {
  const s = STATUS_STYLE[status];
  return (
    <span
      className="px-2.5 py-0.5 rounded-full text-xs"
      style={{ background: s.bg, color: s.fg, fontWeight: 700, letterSpacing: "0.04em" }}
    >
      {C.statusLabel(status)}
    </span>
  );
}

export function roleLabelOf(user: Pick<ClientUser, "adminRole">): string {
  return CLIENT_ADMIN_ROLE_LABELS[user.adminRole ?? "user"];
}

export function AccessSummary({ user }: { user: ClientUser }): JSX.Element {
  // Admin görünümü: kapsam dışı set/erişim ayrıntısı gelmez, yalnız işaret (spec §5).
  const outOfScope =
    user.hiddenSetCount > 0 ? (
      <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
        {C.access.hiddenSets(user.hiddenSetCount)}
      </div>
    ) : user.outOfScopeAccess ? (
      <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
        {C.access.outOfScopeMark}
      </div>
    ) : null;
  if (user.kpiAccess.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <span style={{ color: "var(--color-text-3)" }}>{C.access.none}</span>
        {outOfScope}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {user.kpiAccess.map((a) => (
        <div key={a.setId} className="text-sm">
          <strong style={{ color: "var(--color-text)" }}>{a.setName}</strong>
          <span style={{ color: "var(--color-text-2)" }}> · {accessSummaryLine(a, C.access)}</span>
          {a.outOfScope ? (
            <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
              {" "}
              · {C.access.outOfScopeMark}
            </span>
          ) : null}
        </div>
      ))}
      {outOfScope}
    </div>
  );
}

export function ClientUserCards({
  users,
  selectedId,
  meId,
  actions,
}: {
  users: ClientUser[];
  selectedId: string | null;
  meId: string | null;
  actions: (u: ClientUser, isSelected: boolean) => JSX.Element;
}): JSX.Element {
  return (
    <div
      className="flex flex-col gap-3 px-4 pb-4"
      data-testid="client-users-cards"
      aria-label={C.columns.user}
    >
      {users.length === 0 ? (
        <div className="py-6 text-center text-sm" style={{ color: "var(--color-text-3)" }}>
          {C.empty.noMatch}
        </div>
      ) : (
        users.map((u) => {
          const closed = u.status === "closed";
          const isSelected = u.id === selectedId;
          return (
            <article
              key={u.id}
              aria-label={u.fullName}
              className="rounded-lg p-3 text-sm"
              style={{
                border: "1px solid var(--color-border)",
                background: isSelected
                  ? "color-mix(in srgb, var(--color-primary) 5%, white)"
                  : "var(--color-surface)",
                opacity: closed ? 0.6 : 1,
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div style={{ minWidth: 0 }}>
                  <div className="truncate" style={{ color: "var(--color-text)", fontWeight: 600 }}>
                    {u.fullName}
                    {u.isAdmin ? (
                      <span
                        className="ml-2 text-xs"
                        style={{ color: "var(--color-primary)", fontWeight: 600 }}
                      >
                        {C.adminTag}
                        {u.id === meId ? ` · ${C.youTag}` : ""}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs truncate" style={{ color: "var(--color-text-3)" }}>
                    {u.email}
                  </div>
                </div>
                <StatusPill status={u.status} />
              </div>
              <dl
                className="grid gap-x-3 gap-y-1 mt-3 text-xs"
                style={{ gridTemplateColumns: "minmax(88px, auto) 1fr" }}
              >
                <dt style={{ color: "var(--color-text-3)" }}>{C.admins.roleColumn}</dt>
                <dd style={{ margin: 0, color: "var(--color-text-2)" }}>{roleLabelOf(u)}</dd>
                <dt style={{ color: "var(--color-text-3)" }}>{C.cards.kpi}</dt>
                <dd style={{ margin: 0 }}>
                  <AccessSummary user={u} />
                </dd>
                <dt style={{ color: "var(--color-text-3)" }}>{C.cards.forms}</dt>
                <dd style={{ margin: 0, color: "var(--color-text-2)" }}>
                  {u.projects.length === 0
                    ? C.forms.none
                    : u.projects.map((p) => (
                        <div key={p.projectId}>
                          {p.label} · {C.forms.status[p.participantStatus] ?? p.participantStatus}
                        </div>
                      ))}
                </dd>
              </dl>
              <div className="flex justify-end mt-2">{actions(u, isSelected)}</div>
            </article>
          );
        })
      )}
    </div>
  );
}
